import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { differenceBy } from 'lodash-es';
import { catchError, filter, finalize, forkJoin, Observable, of, Subject, Subscription, switchMap, take, takeUntil, tap } from 'rxjs';
import { SearchPageService } from '../search-page.service';
import { SearchFormType } from '../search-page-type.enum';
import { ISearchParamLayout, PostLoadingSelection, SearchParams, SearchTypeInfo } from '../search-param.model';
import {
    seToolbarDisableAllButtons,
    seToolbarSetButton,
} from '../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.actions';
import { RootState } from '../../root-store/root.state';
import {
    seSearch,
    seSearchCompleted,
    seSearchIfFiltersEq,
    seSearchSafeExit,
    seSearchSetDiff,
    seSearchSetItems,
    seSearchToggleLeftArea,
    seSearchToggleRightArea,
    seSearchToggleTimeline,
    seSearchTypeChange,
} from '../redux/search-page.actions';
import { selectSeSearchItems, selectSeSearchSelectedItems } from '../redux/search-page.selectors';
import { SearchPageConfigurationService } from '../search-page-configuration.service';
import { seSidePatientHistoryComponentNoResult } from '../../se-side/se-side-components/se-side-patient-history/redux/se-side-patient-history.actions';
import { SearchPageDiff } from '../redux/search-page.state';
import { UserConfigurationSettingsService } from '../../services/settings/user-configuration-settings.service';
import { ButtonsTypes, SearchGridUserPreference, UserConfiguration, VNextGridUserPreference } from 'estensa-module';
import { JsonObject } from '@se/cp.common';
import { RowSelectionOptions } from 'ag-grid-community';
import { ModalDialogNotificationService, NotificationService } from '../../services/notification.service';
import { Logger } from '@se/se.platform.base';
import { ButtonClick } from '../../dialogs/se-modalwindow/se-modalwindow.component';
import { SeResignModalService } from '../../se-resign-modal/se-resign-modal.service';
import { ExtraButton, ToolbarAction } from '../../se-toolbar/se-buttons-toolbar/types';
import { IVisitWorklistSearchingParams, SearchCda } from '../../models';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { applyVisitExaminationValidation } from '../../validation/visit.validation';
import { applyEcgExaminationValidation } from '../../validation/ecg.validation';
import { applyNreValidation } from '../../validation/nre.validation';
import { applyExaminationWorklistValidation } from '../../validation/worklist.validation';

interface DoSearchParams {
    highlightRow: boolean;
    showDiff: boolean;
}

@Component({
    selector: 'se-search-container',
    templateUrl: './search-container.component.html',
    styleUrls: ['./search-container.component.scss'],
    standalone: false,
})
export class SearchContainerComponent implements OnInit, AfterViewInit, OnDestroy {
    disableSearch: boolean = false;
    actualSearchType?: SearchTypeInfo;
    hideFilters: boolean = false;
    searchResult$?: Observable<JsonObject[]>;
    favorites: VNextGridUserPreference[] | null = null;
    newFavoriteFilterPopup = false;
    newFavoriteFilterData = { name: '', isDefault: false };
    cdaSearchFormType = [SearchFormType.VISIT, SearchFormType.ECG, SearchFormType.SPECIALTY];
    private cdaSettingsVisiblity = false;
    private destroyed$ = new Subject<void>();
    //private saveableFilters = ['idBusinessUnits', 'idProvenances', 'idModality', 'idCustomPageRoots', 'idEquipments', 'idPatientAssociation'];
    private notSaveableFilters = ['name', 'surname', 'birthDate', 'patientId', 'otherPatientId', 'examNumber'];
    _formType!: SearchFormType;
    get formType() {
        return this._formType;
    }

    @Input()
    set formType(value: SearchFormType) {
        this._formType = value;
        if (value) this.searchResult$ = this.store.pipe(select(selectSeSearchItems(value)));
    }

    @Input()
    loadingTitle: string = this.translate.instant('Loading');

    @Input()
    rowSelection: RowSelectionOptions = {
        mode: 'singleRow',
        checkboxes: false, // elimina le checkbox che compaiono di default su ogni riga
        enableClickSelection: true, // abilita la selezione con il click - obbligatorio se si eliminano le checkbox
        enableSelectionWithoutKeys: false, // se true, la multi selezione si può fare solo con il click - se false, è necessario premere ctrl
    };

    @Input()
    keepSelectionAfterSearch = false;

    @Input()
    postLoadingSelectionOps: PostLoadingSelection = PostLoadingSelection.firstItem;

    @Input()
    idPropertyName: string = 'id'; // Nome della proprietà che contiene l'identificativo univoco di un oggetto che rappresetna il dato dcontenuto in una riga

    _isBusy: boolean = true;
    get isBusy(): boolean {
        return this._isBusy;
    }

    @Input()
    set isBusy(value: boolean) {
        let delay = value ? 0 : 1000;
        setTimeout(() => {
            this._isBusy = value;
            if (this._isBusy) {
                this.store.dispatch(seToolbarDisableAllButtons());
            }
        }, delay);
    }

    @Input()
    searchOnInit: boolean = true;

    @Input()
    canItemBeSelected?: (item: JsonObject, actualSelectedItems: JsonObject[]) => boolean;

    fnCanItemBeSelectedHandler = (itemToSelect: JsonObject, actualSelectedItems: JsonObject[]): boolean => {
        if (this.canItemBeSelected) {
            return this.canItemBeSelected(itemToSelect, actualSelectedItems);
        }

        return true;
    };

    @Input()
    canItemDoubleClicked?: (item: JsonObject, actualSelectedItems: JsonObject[]) => boolean;

    fnCanItemDoubleClickedHandler = (itemToSelect: JsonObject, actualSelectedItems: JsonObject[]) => {
        if (this.canItemDoubleClicked) {
            return this.canItemDoubleClicked(itemToSelect, actualSelectedItems);
        }

        return true;
    };

    @Input()
    canPatientHistoryOpenForSelection: Function = (actualSelectedItems: JsonObject[]) => {
        return actualSelectedItems && actualSelectedItems.length == 1; // By default si apre solo se c'è una sola selezione
    };

    fnCanPatientHistoryOpenForSelectionHandler = (actualSelectedItems: JsonObject[]) => {
        if (this.canPatientHistoryOpenForSelection) {
            return this.canPatientHistoryOpenForSelection(actualSelectedItems);
        }

        return false;
    };

    @Input()
    filterChangeOps?: (
        propertyName: string,
        propertyValue: any,
        params: Record<string, any>,
        layouts: Record<string, ISearchParamLayout>,
    ) => any;

    fnFilterChangeOpsHandler = (
        propertyName: string,
        propertyValue: any,
        params: Record<string, any>,
        layouts: Record<string, ISearchParamLayout>,
    ) => {
        if (this.filterChangeOps) {
            return this.filterChangeOps(propertyName, propertyValue, params, layouts);
        }
    };

    @Input()
    itemSourceOps?: (propertyName: string, params: any, itemSource: any[]) => any[];

    fnItemSourceOpsHandler = (propertyName: string, params: any, itemSource: any[]) => {
        if (this.itemSourceOps) {
            return this.itemSourceOps(propertyName, params, itemSource);
        }

        return itemSource;
    };

    @Input()
    getTooltipForSelection?: (rowData: JsonObject) => any;

    fnGetTooltipForSelectionHandler = (rowData: JsonObject) => {
        if (this.getTooltipForSelection) {
            return this.getTooltipForSelection(rowData);
        }

        return null;
    };

    @Input()
    getButtonContentForSelection?: (rowData: JsonObject) => any;

    fnGetButtonContentForSelectionHandler = (rowData: JsonObject) => {
        if (this.getButtonContentForSelection) {
            return this.getButtonContentForSelection(rowData);
        }

        return null;
    };

    @Input()
    getItemDiff?: (item: any) => SearchPageDiff;

    get searchParams(): SearchParams {
        return this.searchPageService.getSearchParams(this.formType);
    }

    get searchTypes(): SearchTypeInfo[] {
        const types = this.searchPageConfigurationService.getSearchTypes(this.formType);

        if (!types || types.length < 1) {
            return [];
        }

        return types;
    }

    get isMultiSearchEnabled() {
        return !!this.searchTypes && this.searchTypes.length > 0;
    }

    get disabledShowHideIdList(): string[] {
        // Lista degli id relativi alle colonne che non devono essere inseriti nel menu' Show\Hide
        return [
            'lockColId',
            'ExamOutOfFlow',
            'CompleteCdStatusColId',
            'PrintingCdStatusColId',
            'AssociatedVisit',
            'SameStudy',
            'AttachedDocuments',
            'AssociatedSpecialty'
        ];
    }

    get runValidation(): boolean {
        //@ts-ignore
        return Boolean(window['activateValidation']);
    }

    _actionSubscription?: Subscription;

    _hideFiltersSubscription?: Subscription;
    _hideRightAreaSubscription?: Subscription;
    _hideTimelineSubscription?: Subscription;
    _searchTypeSubscription?: Subscription;
    _buttonsToolbarSubscription?: Subscription;

    // Constructor
    constructor(
        private searchPageConfigurationService: SearchPageConfigurationService,
        private searchPageService: SearchPageService,
        private notificationService: NotificationService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translate: TranslateService,
        private actions: Actions,
        private store: Store<RootState>,
        private userConfigurationSettingsService: UserConfigurationSettingsService,
        private logger: Logger,
        private resignService: SeResignModalService,
        private authenticationService: AuthenticationService,
    ) {}

    // public functions

    onChangeSearchType = (searchType: SearchTypeInfo) => {
        this.store.dispatch(seSearch({ formType: this.formType, searchType }));
    };

    ngOnInit() {
        this.store.dispatch(seSearchTypeChange({ formType: this.formType }));
        // Prima leggo lo stato salvato degli slider e poi attacco i listener
        this.listenForStoreEvents();
        this.getFavoriteFilters();
    }

    ngAfterViewInit() {
        if (!this.searchOnInit) {
            setTimeout(() => {
                this.isBusy = false;
            });
        }
        this.getCDA();
    }

    ngOnDestroy() {
        this._actionSubscription?.unsubscribe();
        this._hideFiltersSubscription?.unsubscribe();
        this._hideRightAreaSubscription?.unsubscribe();
        this._hideTimelineSubscription?.unsubscribe();
        this._searchTypeSubscription?.unsubscribe();
        this.destroyed$.next();
        this.destroyed$.complete();
        this.store.dispatch(seSearchSafeExit({ formType: this.formType }));
    }

    onSearch() {
        this.store.dispatch(seSearch({ formType: this.formType }));
    }

    private getResults() {
        return this.store.pipe(
            select('searchPage', this.formType, 'actualSearchType'),
            take(1),
            switchMap((searchType) => {
                const searchInfo = {
                    ...this.searchParams.toJSON(),
                    searchType: searchType ? searchType.id : null,
                } as IVisitWorklistSearchingParams;

                return this.searchPageService.getResult(this.formType, searchInfo);
            }),
            catchError((e) => {
                // TooMuch??
                if (e?.error?.estensaFaultDetails?.typeName == 'TooMuchResultFault') {
                    try {
                        let messageKey = 'Exception_' + e.error.estensaFaultDetails.typeName;

                        let params: any = {};
                        let position = 0;
                        for (let param in e.error.estensaFaultDetails.estensaFault) {
                            if (param === 'errorId' || param === 'message' || param === 'completeMessage') {
                                continue;
                            }
                            params['p' + position] = e.error.estensaFaultDetails.estensaFault[param];
                            position++;
                        }

                        this.modalDialogNotificationService.warning(this.translate.instant(messageKey, params));
                    } catch {
                        this.notificationService.error(
                            e.message ? e.message : '',
                            this.translate.instant('ErrorLoadingData'),
                        );
                    }
                } else
                    this.notificationService.error(
                        e.message ? e.message : '',
                        this.translate.instant('ErrorLoadingData'),
                    );

                // SeSide
                this.seSideFailedSearchOps('ErrorLoadingData');

                return of([]);
            }),
        );
    }

    private doSearch({ highlightRow, showDiff }: DoSearchParams) {
        this.isBusy = true;

        forkJoin([
            this.getResults(),
            this.store.pipe(select(selectSeSearchItems(this.formType)), take(1)),
            this.store.pipe(select(selectSeSearchSelectedItems(this.formType, this.idPropertyName)), take(1)),
        ])
            .pipe(
                tap(([items]) => {
                    if (this.runValidation) {
                        switch (this.formType) {
                            case SearchFormType.VISIT:
                                items.forEach(applyVisitExaminationValidation);
                                break;
                            case SearchFormType.ECG:
                                items.forEach(applyEcgExaminationValidation);
                                break;
                            case SearchFormType.TELEMONITORING:
                                items.forEach(applyVisitExaminationValidation);
                                break;
                            case SearchFormType.SPECIALTY:
                                items.forEach(applyExaminationWorklistValidation);
                                break;
                            case SearchFormType.NRE:
                                items.forEach(applyNreValidation);
                                break;
                        }
                    }
                }),
                finalize(() => {
                    this.isBusy = false;
                }),
            )
            .subscribe(([newItems, oldItems, oldSelectedItems]) => {
                this.store.dispatch(seSearchSetItems({ formType: this.formType, items: newItems }));

                const addedRows = differenceBy(newItems, oldItems, this.idPropertyName);
                const getItemDiff = this.getItemDiff;
                if (showDiff && getItemDiff) {
                    const diff = addedRows.map((item) => getItemDiff(item));
                    this.store.dispatch(
                        seSearchSetDiff({
                            formType: this.formType,
                            diff,
                        }),
                    );
                } else {
                    this.store.dispatch(
                        seSearchSetDiff({
                            formType: this.formType,
                        }),
                    );
                }

                setTimeout(() => {
                    this.store.dispatch(
                        seSearchCompleted({
                            formType: this.formType,
                            addedRows,
                            highlightRow,
                            oldSelectedItems,
                        }),
                    );
                });

                // Comunico al componente side che la ricerca non ha prodotto risultati
                if (!newItems || newItems.length <= 0) {
                    this.seSideFailedSearchOps('NoResults');
                }
            });
    }

    private seSideFailedSearchOps(message: string) {
        // Patient History
        this.store.dispatch(
            seSidePatientHistoryComponentNoResult({
                formType: this.formType,
                message: this.translate.instant(message),
            }),
        );

        // ... Eventuali altri componenti
    }

    onResetFilter() {
        this.searchParams.resetSearchParamToDefault();
    }

    private getFavoriteFilters() {
        let config: UserConfiguration | null = this.userConfigurationSettingsService.userConfiguration;
        if (this.formType === SearchFormType.VISIT) {
            this.favorites = config?.preferences.searchGridUserPreference?.visitWorklistSearchGrid?.collection ?? null;
        } else if (this.formType === SearchFormType.ECG) {
            this.favorites = config?.preferences.searchGridUserPreference?.ecgWorklistSearchGrid?.collection ?? null;
        } else if (this.formType === SearchFormType.SPECIALTY) {
            this.favorites =
                config?.preferences.searchGridUserPreference?.specialtyWorklistSearchGrid?.collection ?? null;
        } else {
            this.favorites = null;
        }
    }

    private resetDefaultFavoriteFilter() {
        this.favorites?.forEach((filter) => {
            filter.isDefault = false;
        });
    }

    private getDateDelta(dateParams: any): number {
        if (dateParams?.from && dateParams?.to) {
            const to = dateParams.to;
            const today = new Date();
            const todayString = today.toISOString().split('T')[0];
            if (to.includes(todayString)) {
                // è oggi
                const from = dateParams.from;
                const fromDate = new Date(from).setHours(0, 0, 0);
                const toDate = new Date(to).setHours(0, 0, 0);
                const differenceInMilliseconds = toDate - fromDate;
                const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
                return differenceInDays;
            }
        }
        return 0;
    }

    public onSaveFavoriteFilter(button: ButtonClick) {
        if (button === ButtonClick.ok) {
            let newFilter = this.cleanFilter();
            if (newFilter.isDefault) {
                this.resetDefaultFavoriteFilter();
            }
            const index =
                this.favorites?.findIndex((object) => {
                    return object.name === newFilter.name;
                }) ?? -1;
            if (index !== -1 && this.favorites) {
                this.favorites[index] = newFilter;
            } else {
                const newCollection = [...(this.favorites ?? []), newFilter];
                this.favorites = newCollection;
            }
            this.saveFavoriteFilter(this.favorites);
        }
        this.newFavoriteFilterData = { name: '', isDefault: false };
        this.newFavoriteFilterPopup = false;
    }

    private cleanFilter() {
        let searchParams = this.searchParams.toJSON();
        const dateDelta = this.getDateDelta(searchParams['date']);
        Object.keys(searchParams).forEach((field) => {
            if (this.notSaveableFilters.includes(field)) {
                delete searchParams[field];
            }
        });
        if (dateDelta) {
            searchParams['dateDelta'] = dateDelta; // gestire saveableFilters anche la data quando sarà configurabile
        }

        const newFilter = {
            isDefault: this.newFavoriteFilterData.isDefault,
            name: this.newFavoriteFilterData.name,
            searchParams: JSON.stringify(searchParams) as any,
        };
        return newFilter;
    }

    private saveFavoriteFilter(newCollection: VNextGridUserPreference[]) {
        let searchGridUserPreference: Partial<SearchGridUserPreference> = {};

        switch (this.formType) {
            case SearchFormType.ECG:
                searchGridUserPreference = {
                    ecgWorklistSearchGrid: {
                        collection: newCollection,
                    },
                };
                break;
            case SearchFormType.VISIT:
                searchGridUserPreference = {
                    visitWorklistSearchGrid: {
                        collection: newCollection,
                    },
                };
                break;
            case SearchFormType.SPECIALTY:
                searchGridUserPreference = {
                    specialtyWorklistSearchGrid: {
                        collection: newCollection,
                    },
                };
                break;
            default:
                return;
        }
        try {
            this.userConfigurationSettingsService.setUserPreferences({ searchGridUserPreference });
            this.notificationService.success(this.translate.instant('FavoriteFilterSaveSuccess'));
        } catch (error) {
            if (error instanceof Error) {
                this.logger?.error(error.message);
            }
            this.notificationService.error(this.translate.instant('FavoriteFilterSaveError'));
        }
    }

    public onActivateFavoriteFilter(filter: VNextGridUserPreference) {
        this.onResetFilter();
        const newParams = JSON.parse(filter.searchParams);
        for (const key in newParams) {
            if (key !== 'dateDelta') {
                this.searchParams.params[key] = newParams[key];
            }
        }
        if (newParams['dateDelta']) {
            this.searchParams.params['date'] = this.searchPageService.calculateParamDate(newParams['dateDelta']);
        }
    }

    public onDeleteFavoriteFilter(filter: VNextGridUserPreference) {
        const index =
            this.favorites?.findIndex((object) => {
                return object.name === filter.name;
            }) ?? -1;
        if (index !== -1) {
            this.favorites?.splice(index, 1);
            this.saveFavoriteFilter(this.favorites ?? []);
        }
    }

    public onSetDefaultFavoriteFilter(filter: VNextGridUserPreference, remove = false) {
        const index =
            this.favorites?.findIndex((object) => {
                return object.name === filter.name;
            }) ?? -1;
        if (index !== -1 && this.favorites) {
            this.resetDefaultFavoriteFilter();
            if (!remove) {
                this.favorites[index].isDefault = true;
            }
            this.saveFavoriteFilter(this.favorites);
        }
    }

    onDisableSearch(disable: boolean) {
        this.disableSearch = disable;
    }

    onBusy(isBusy: boolean) {
        this.isBusy = isBusy;
    }

    private listenForStoreEvents() {
        this._actionSubscription = this.actions
            .pipe(
                ofType(seSearch, seSearchIfFiltersEq),
                filter((action) => {
                    if (action.formType !== this.formType) {
                        return false;
                    }
                    if ('filters' in action) {
                        return this.searchParamsMatch(action.filters);
                    }
                    return true;
                }),
            )
            .subscribe((action) => {
                this.doSearch({
                    highlightRow: action.highlightRow ?? false,
                    showDiff: action.showDiff ?? false,
                });
            });

        this._hideFiltersSubscription = this.store
            .select((state) => state.buttonsToolbar.leftSlider.checked)
            .subscribe((checked) => {
                this.store.dispatch(seSearchToggleLeftArea({ formType: this.formType, hide: !checked })); // Notifico lo stato
                this.hideFilters = !checked; // nascondo la toolbar
            });

        this._hideRightAreaSubscription = this.store
            .select((state) => state.buttonsToolbar.rightSlider.checked)
            .subscribe((checked) => {
                this.store.dispatch(seSearchToggleRightArea({ formType: this.formType, hide: checked })); // Notifico lo stato
            });

        this._hideTimelineSubscription = this.store
            .select((state) => state.buttonsToolbar.timeline?.open ?? false)
            .subscribe((open) => {
                this.store.dispatch(seSearchToggleTimeline({ formType: this.formType, hide: open })); // Notifico lo stato
            });

        this._searchTypeSubscription = this.store
            .select((state) => state.searchPage[this.formType]?.actualSearchType)
            .subscribe((st) => (this.actualSearchType = st));

        this._buttonsToolbarSubscription = this.store
            .select((state) => state.buttonsToolbar)
            .subscribe((buttons) => {
                const toolbarButtons = buttons.buttons;
                if (toolbarButtons && toolbarButtons.ReSign) {
                    this.cdaSettingsVisiblity = toolbarButtons.ReSign.status.visible;
                }
            });
    }

    private searchParamsMatch(filters: Record<string, any>) {
        const searchParams = this.searchParams;

        for (const filterKey in filters) {
            if (
                Object.hasOwnProperty.call(filters, filterKey) &&
                !searchParams.matches(filterKey, filters[filterKey])
            ) {
                return false;
            }
        }
        return true;
    }

    getCDA() {
        if (this.cdaSearchFormType.includes(this.formType)) {
            const searchInfo: SearchCda = {
                idReferringPhysicians: [this.authenticationService.getUserId()],
                onlyResigned: true,
                searchType: 0,
            };
            this.searchPageService.getWorklistCDA(this.formType, searchInfo)
                .pipe(take(1), takeUntil(this.destroyed$))
                .subscribe({
                    next: (cda: any[]) => {
                        if (cda.length) {
                            this.store.dispatch(
                                seToolbarSetButton({
                                    action: ToolbarAction.RESIGN,
                                    button: {
                                        actionType: ToolbarAction.RESIGN,
                                        permission: ButtonsTypes.reSign,
                                        isToggle: false,
                                        status: {
                                            visible: this.cdaSettingsVisiblity,
                                            disabled: false,
                                        },
                                        extrasButton: {
                                            type: ExtraButton.BADGE,
                                            value: cda.length,
                                        }
                                    }
                                })
                            );
                            this.resignService.setCDADataForGrid(cda);
                        } else {
                            this.store.dispatch(
                                seToolbarSetButton({
                                    action: ToolbarAction.RESIGN,
                                    button: {
                                        actionType: ToolbarAction.RESIGN,
                                        permission: ButtonsTypes.reSign,
                                        isToggle: false,
                                        status: {
                                            visible: this.cdaSettingsVisiblity,
                                            disabled: true,
                                        },
                                        extrasButton: null
                                    }
                                })
                            );
                            this.resignService.setCDADataForGrid(cda);
                        }
                },
                error: (error) => {
                    this.store.dispatch(
                        seToolbarSetButton({
                            action: ToolbarAction.RESIGN,
                            button: {
                                actionType: ToolbarAction.RESIGN,
                                permission: ButtonsTypes.reSign,
                                isToggle: false,
                                status: {
                                    visible: true,
                                    disabled: true,
                                },
                                extrasButton: null
                            },
                        }),
                    );
                    this.resignService.setCDADataForGrid([]);
                    this.modalDialogNotificationService.error(this.translate.instant('ErrorDuringCDARequest'));
                },
            });
        }
    }
}
